import {useSelector} from '@tanstack/react-form';
import * as React from 'react';

import {useFormContext} from './context';
import {toFormApi} from './formApi';
import type {FormApi, FormState} from './types';

/**
 * A read-only view of the whole form. This re-renders on any change anywhere in
 * the form, so prefer `useFieldState` when one field will do.
 */
export const useFormState = (): FormState => {
  const {form} = useFormContext();
  // Read field meta from the base store rather than the derived one: removing
  // a field edits the base map in place, which the derived copy can miss, and
  // it would go on reporting a field that is no longer part of the form.
  const state = useSelector(form.baseStore);
  return React.useMemo(
    () => ({
      touched: Object.fromEntries(
        Object.entries(state.fieldMetaBase).map(([name, meta]) => [
          name,
          Boolean(meta?.isTouched),
        ]),
      ),
      values: state.values,
    }),
    [state],
  );
};

/**
 * An imperative handle on the whole form, for reading and writing fields the
 * caller doesn't render. This doesn't subscribe to anything, so it won't
 * re-render the caller when the form changes.
 */
export const useFormApi = (): FormApi => {
  const {form} = useFormContext();
  return React.useMemo(() => toFormApi(form), [form]);
};
